import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from "antd";
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import urls from "urls";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import {
  FIRESTORE_CAMPAIGNS_TABLE,
  FIRESTORE_DISCOUNT_CODES_TABLE,
  FIRESTORE_UTMS_TABLE,
} from "constants/FirestoreConstant";
import { useCampaign } from "data/hooks/useCampaigns";
import { useProducts } from "data/hooks/useProducts";
import { useCategories } from "data/hooks/useCategories";
import { useUTMs } from "data/hooks/useUTMs";
import UTMFormFields from "components/marketing/UTMFormFields";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// Predefined channel options
const CHANNEL_OPTIONS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "google", label: "Google" },
  { value: "email", label: "Email" },
  { value: "tiktok", label: "TikTok" },
];

const CampaignForm = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isOngoing, setIsOngoing] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const shopSource = searchParams.get("shopSource") || "VCE";

  const { campaign, isLoading: campaignLoading } = useCampaign(isEdit ? id : null, isEdit ? shopSource : null);
  const { products, isLoading: productsLoading } = useProducts();
  const { categories, isLoading: categoriesLoading } = useCategories();
  const { utms, isLoading: utmsLoading, refetch: refetchUtms } = useUTMs({ shopSource: shopSource });
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountCodesLoading, setDiscountCodesLoading] = useState(false);
  const [selectedUtmIds, setSelectedUtmIds] = useState([]);
  const [currentUtmSelection, setCurrentUtmSelection] = useState(null);
  const [showNewUtmForm, setShowNewUtmForm] = useState(false);
  const [newUtmForm] = Form.useForm();
  const [useCustomUrl, setUseCustomUrl] = useState(false);

  useEffect(() => {
    if (isEdit && campaign) {
      const convertToDate = (dateValue) => {
        if (!dateValue) return null;
        if (dateValue.toDate && typeof dateValue.toDate === "function") {
          return dayjs(dateValue.toDate());
        }
        if (dateValue instanceof Date) {
          return dayjs(dateValue);
        }
        return dayjs(dateValue);
      };

      const formData = {
        ...campaign,
        startDate: convertToDate(campaign.startDate),
        endDate: convertToDate(campaign.endDate),
      };
      form.setFieldsValue(formData);
      setIsOngoing(campaign.isOngoing || false);
      setSelectedUtmIds(campaign.utmIds || []);
    }
  }, [form, isEdit, campaign]);

  // Fetch discount codes when shop source changes
  useEffect(() => {
    const fetchDiscountCodes = async () => {
      if (!shopSource) return;

      setDiscountCodesLoading(true);
      try {
        const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
        const snapshot = await firestore.collection(FIRESTORE_DISCOUNT_CODES_TABLE).get();
        const codes = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDiscountCodes(codes);
      } catch (error) {
        console.error("Error fetching discount codes:", error);
      } finally {
        setDiscountCodesLoading(false);
      }
    };

    fetchDiscountCodes();
  }, [shopSource]);

  // Watch shop source changes from form
  const selectedShopSource = Form.useWatch("shopSource", form) || shopSource;

  const transformToUtmValue = (input) => {
    if (!input) return "";
    return input
      .toLowerCase()
      .replace(/[^a-z0-9_\s]/g, "")
      .replace(/\s+/g, "_")
      .trim();
  };

  const handleAddNewUtm = async () => {
    try {
      const values = await newUtmForm.validateFields();
      const firestore = selectedShopSource === "VCE" ? vceFirestore : hvoFirestore;

      const utmData = {
        name: values.name,
        source: values.sourceType === "other" ? values.sourceCustom : values.sourceType,
        medium: values.mediumType === "other" ? values.mediumCustom : values.mediumType,
        campaign: transformToUtmValue(values.campaign),
        term: transformToUtmValue(values.term),
        content: transformToUtmValue(values.content),
        customUrl: useCustomUrl ? values.customUrl || null : null,
        createdAt: dayjs().unix(),
      };

      const docRef = await firestore.collection(FIRESTORE_UTMS_TABLE).add(utmData);
      message.success(t("utm_saved"));

      refetchUtms();
      setSelectedUtmIds([...selectedUtmIds, docRef.id]);
      setShowNewUtmForm(false);
      newUtmForm.resetFields();
      setUseCustomUrl(false);
    } catch (error) {
      console.error("Error saving UTM:", error);
      message.error(t("error_saving_utm"));
    }
  };

  const handleRemoveUtm = (utmId) => {
    setSelectedUtmIds(selectedUtmIds.filter((id) => id !== utmId));
  };

  const handleAddUtm = (utmId) => {
    if (utmId && !selectedUtmIds.includes(utmId)) {
      setSelectedUtmIds([...selectedUtmIds, utmId]);
      setCurrentUtmSelection(null);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const firestore = values.shopSource === "VCE" ? vceFirestore : hvoFirestore;

      const campaignData = {
        name: values.name || "",
        description: values.description || "",
        platform: values.platform || "",
        status: values.status || "draft",
        campaignType: values.campaignType || "",
        campaignGoal: values.campaignGoal || "",
        budget: values.budget || null,
        shopSource: values.shopSource || "VCE",
        startDate: values.startDate ? values.startDate.toDate() : null,
        endDate: isOngoing ? null : values.endDate ? values.endDate.toDate() : null,
        isOngoing: isOngoing,
        utmIds: selectedUtmIds,
        discountCodes: values.discountCodes || [],
        targetProducts: values.targetProducts || [],
        targetCategories: values.targetCategories || [],
        channels: values.channels || [],
        mailType: values.mailType || null,
        triggerDays: values.triggerDays || null,
        updatedAt: dayjs().unix(),
      };

      if (isEdit) {
        await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(id).update(campaignData);
        message.success(t("campaign_updated"));
      } else {
        await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).add({
          ...campaignData,
          createdAt: dayjs().unix(),
        });
        message.success(t("campaign_created"));
      }

      navigate(urls.campaigns);
    } catch (error) {
      console.error("Error saving campaign:", error);
      message.error(t("error_saving_campaign"));
    } finally {
      setLoading(false);
    }
  };

  const handleOngoingChange = (checked) => {
    setIsOngoing(checked);
    if (checked) {
      form.setFieldsValue({ endDate: null });
    }
  };

  if (isEdit && campaignLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.campaigns)}>
              {t("back")}
            </Button>
            <h2>{isEdit ? t("edit_campaign") : t("add_campaign")}</h2>
          </Space>
        </Col>
      </Row>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: "draft",
            platform: "meta_ads",
            isOngoing: false,
            shopSource: shopSource,
            goal: "acquisition",
            type: "product_discount",
            channels: [],
            discountCodes: [],
            targetProducts: [],
            targetCategories: [],
            utmIds: [],
            mailType: null,
            triggerDays: null,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("campaign_name")}
                name="name"
                rules={[{ required: true, message: t("please_enter_campaign_name") }]}
              >
                <Input placeholder={t("enter_campaign_name")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("shop_source")}
                name="shopSource"
                rules={[{ required: true, message: t("please_select_shop_source") }]}
              >
                <Select placeholder={t("select_shop_source")} disabled={isEdit}>
                  <Option value="VCE">VCE - Vleescentrum</Option>
                  <Option value="HVO">HVO - Halalvlees Online</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("platform")}
                name="platform"
                rules={[{ required: true, message: t("please_select_platform") }]}
              >
                <Select placeholder={t("select_platform")}>
                  <Option value="meta_ads">{t("meta_ads")}</Option>
                  <Option value="google_ads">{t("google_ads")}</Option>
                  <Option value="email_marketing">{t("email_marketing")}</Option>
                  <Option value="other">{t("other")}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t("campaign_description")} name="description">
            <TextArea rows={4} placeholder={t("enter_campaign_description")} />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("campaign_type")}
                name="type"
                rules={[{ required: true, message: t("please_select_campaign_type") }]}
              >
                <Select placeholder={t("select_campaign_type")}>
                  <Option value="product_discount">{t("product_discount")}</Option>
                  <Option value="category_promo">{t("category_promo")}</Option>
                  <Option value="seasonal">{t("seasonal")}</Option>
                  <Option value="winback">{t("winback")}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("campaign_goal")}
                name="goal"
                rules={[{ required: true, message: t("please_select_campaign_goal") }]}
              >
                <Select placeholder={t("select_campaign_goal")}>
                  <Option value="acquisition">{t("acquisition")}</Option>
                  <Option value="retention">{t("retention")}</Option>
                  <Option value="upsell">{t("upsell")}</Option>
                  <Option value="clearance">{t("clearance")}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("status")}
                name="status"
                rules={[{ required: true, message: t("please_select_status") }]}
              >
                <Select placeholder={t("select_status")}>
                  <Option value="draft">{t("draft")}</Option>
                  <Option value="active">{t("active")}</Option>
                  <Option value="paused">{t("paused")}</Option>
                  <Option value="completed">{t("completed")}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label={t("budget")} name="budget">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder={t("enter_budget")}
                  min={0}
                  step={10}
                  formatter={(value) => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/€\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={6}>
              <Form.Item label={t("mail_type")} name="mailType" help={t("mail_type_help")}>
                <Select placeholder={t("select_mail_type")} allowClear>
                  <Option value="single_buyer_winback">{t("single_buyer_winback")}</Option>
                  <Option value="loyal_sleeper_winback">{t("loyal_sleeper_winback")}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("trigger_days")}
                name="triggerDays"
                help={t("trigger_days_help")}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder={t("enter_trigger_days")}
                  min={0}
                  step={1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label={t("start_date")}
                name="startDate"
                rules={[{ required: true, message: t("please_select_start_date") }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t("ongoing")}>
                <Switch
                  checked={isOngoing}
                  onChange={handleOngoingChange}
                  checkedChildren={t("ongoing")}
                  unCheckedChildren={t("end_date")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              {!isOngoing && (
                <Form.Item
                  label={t("end_date")}
                  name="endDate"
                  rules={[
                    { required: !isOngoing, message: t("please_select_end_date") },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || !getFieldValue("startDate")) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(getFieldValue("startDate"))) {
                          return Promise.reject(new Error(t("end_date_must_be_after_start_date")));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item label={t("objectives")} name="objectives">
            <TextArea rows={3} placeholder={t("enter_campaign_objectives")} />
          </Form.Item>

          <Card title={t("campaign_attribution")} size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label={t("discount_codes")} name="discountCodes" help={t("discount_codes_help")}>
                  <Select
                    mode="tags"
                    placeholder={t("enter_discount_codes")}
                    tokenSeparators={[","]}
                    loading={discountCodesLoading}
                  >
                    {discountCodes.map((code) => (
                      <Option key={code.id} value={code.code || code.id}>
                        {code.code || code.id} {code.description && `- ${code.description}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t("channels")} name="channels" help={t("channels_help")}>
                  <Select mode="tags" placeholder={t("enter_channels")} tokenSeparators={[","]}>
                    {CHANNEL_OPTIONS.map((channel) => (
                      <Option key={channel.value} value={channel.value}>
                        {channel.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t("target_products")} name="targetProducts" help={t("target_products_help")}>
                  <Select
                    mode="tags"
                    placeholder={t("enter_product_ids")}
                    tokenSeparators={[","]}
                    loading={productsLoading}
                    showSearch
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  >
                    {products
                      .filter((p) => p.shopSource === selectedShopSource)
                      .map((product) => (
                        <Option key={product.id} value={product.id}>
                          {product.name || product.title}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t("target_categories")} name="targetCategories" help={t("target_categories_help")}>
                  <Select
                    mode="tags"
                    placeholder={t("enter_categories")}
                    tokenSeparators={[","]}
                    loading={categoriesLoading}
                    showSearch
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  >
                    {categories
                      .filter((c) => c.shopSource === selectedShopSource)
                      .map((category) => (
                        <Option key={category.id} value={category.id}>
                          {category.name || category.title}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title={t("utm_parameters")} size="small" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <Form.Item label={t("linked_utms")} help={t("select_existing_utms_or_create_new")}>
                <Select
                  placeholder={t("select_utms")}
                  value={currentUtmSelection}
                  onChange={handleAddUtm}
                  loading={utmsLoading}
                  style={{ width: "100%" }}
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                  {utms
                    .filter((utm) => utm.shopSource === selectedShopSource && !selectedUtmIds.includes(utm.id))
                    .map((utm) => (
                      <Option key={utm.id} value={utm.id}>
                        {utm.name} ({utm.source} / {utm.medium} / {utm.campaign})
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              {selectedUtmIds.length > 0 && (
                <div style={{ marginTop: 36 }}>
                  <Text strong>{t("selected_utms")}:</Text>
                  <div
                    style={{
                      marginTop: 12,
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 300px))",
                      gap: "12px",
                    }}
                  >
                    {selectedUtmIds.map((utmId) => {
                      const utm = utms.find((u) => u.id === utmId);
                      if (!utm) return null;
                      return (
                        <Card key={utmId} size="small" style={{ maxWidth: 300, backgroundColor: "#fdfdfd" }}>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <Text strong>{utm.name}</Text>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemoveUtm(utmId)}
                                size="small"
                              />
                            </div>
                            <div style={{ marginTop: 8, fontSize: "13px" }}>
                              <div>
                                <Text type="secondary">Source:</Text> {utm.source}
                              </div>
                              <div>
                                <Text type="secondary">Medium:</Text> {utm.medium}
                              </div>
                              <div>
                                <Text type="secondary">Campaign:</Text> {utm.campaign}
                              </div>
                              {utm.term && (
                                <div>
                                  <Text type="secondary">Term:</Text> {utm.term}
                                </div>
                              )}
                              {utm.content && (
                                <div>
                                  <Text type="secondary">Content:</Text> {utm.content}
                                </div>
                              )}
                              {utm.customUrl && (
                                <div>
                                  <Text type="secondary">Custom URL:</Text> {utm.customUrl}
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => setShowNewUtmForm(!showNewUtmForm)}
                style={{ marginTop: 16, width: "100%" }}
              >
                {showNewUtmForm ? t("hide_new_utm_form") : t("add_new_utm")}
              </Button>
            </div>

            {showNewUtmForm && (
              <Card type="inner" title={t("create_new_utm")} style={{ marginTop: 16 }}>
                <Form
                  form={newUtmForm}
                  layout="vertical"
                  initialValues={{
                    sourceType: "facebook",
                    mediumType: "social",
                    shopSource: selectedShopSource,
                  }}
                >
                  <UTMFormFields
                    form={newUtmForm}
                    useCustomUrl={useCustomUrl}
                    setUseCustomUrl={setUseCustomUrl}
                    showShopSource={false}
                    shopSourceDisabled={true}
                  />
                  <Form.Item>
                    <Space>
                      <Button type="primary" onClick={handleAddNewUtm} icon={<SaveOutlined />}>
                        {t("save_utm")}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowNewUtmForm(false);
                          newUtmForm.resetFields();
                          setUseCustomUrl(false);
                        }}
                      >
                        {t("cancel")}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>
            )}
          </Card>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                {isEdit ? t("update_campaign") : t("create_campaign")}
              </Button>
              <Button onClick={() => navigate(urls.campaigns)}>{t("cancel")}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CampaignForm;
